import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/core';

import { setNodeAttrsAt } from '../tiptap/utils/setNodeAttrsNoHistory';
import { runPendingEditorOperation } from '../helpers/pendingEditorOperations.helper';

/**
 * Whether the backend has this node registered for the version being edited.
 *
 * `unknown` is for nodes the cache deliberately does not track - a SELF reference, which `useAddRef`
 * skips when patching `doc.refsData` - so absence there says nothing.
 */
export type TRegistrationState = 'registered' | 'missing' | 'unknown';

/**
 * Ids this session inserted, and only those - a refused registration undoes an insert, and nothing
 * else. Filled by the `insertBlock` / `insertInlineBlock` / `insertReference` commands, where the id
 * is created, because that is the only place that knows the node is new.
 *
 * Asking the node view instead does not work: it sees a node, not where the node came from. Deriving
 * it from `doc.blocksData` - "the document has no record, so it must be new" - reads exactly false in
 * the case that matters, because a missing record IS the failure being repaired. That cost 17 blocks
 * of stored content in one soak run, deleted six seconds after they appeared.
 *
 * A pasted node is deliberately absent: `CustomUniqueId` gives it a fresh id without going through a
 * command, so a paste whose registration fails keeps the content and shows the error.
 */
const insertedThisSession = new Set<string>();

export const markInserted = (id: string) => insertedThisSession.add(id);

type TOptions = {
  id: string;
  /** `data-loaded-id`: the id this node was last registered under. */
  loadedId?: string;
  isEdit: boolean;
  registration: TRegistrationState;
  register: () => Promise<unknown>;
  editor: Editor;
  getPos: () => number | undefined;
  removeNode: () => void;
};

/**
 * Keep a reference or block node and its backend registration in agreement. Shared by three node
 * types that each got it subtly wrong on their own.
 *
 * `loadedId` is the node's record that it once saw its registration settle - not proof of it.
 * Await the mutation and its cache update before settling the editor operation. Per-call mutation
 * callbacks disappear on unmount; the promise still finishes even if the node has since been removed.
 *
 * Asking "is it in the cache?" on the next mount answers yes and repairs nothing. Asking whether it
 * is registered FOR THIS VERSION is what repairs it.
 */
export const useNodeRegistration = ({
  id,
  loadedId,
  isEdit,
  registration,
  register,
  editor,
  getPos,
  removeNode,
}: TOptions) => {
  // One attempt per id: a refused registration cannot loop, and a same-type replacement reuses this view.
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (!isEdit) return;

    // Registering is the system's work, not the user's, so one Ctrl+Z never reverts it.
    const recordLoaded = () => setNodeAttrsAt(editor.view, getPos, { loadedId: id });

    const believesRegistered = loadedId === id;
    if (registration === 'registered' || (registration === 'unknown' && believesRegistered)) {
      if (!believesRegistered) recordLoaded();
      return;
    }

    if (attempted.current === id) return;
    attempted.current = id;
    void runPendingEditorOperation(editor, async () => {
      let error: unknown;
      try {
        await register();
      } catch (registrationError) {
        error = registrationError;
      }
      // Awaiting the mutation survives a node-view unmount; a removed node needs no attribute write.
      if (!editor.isDestroyed && getPos() !== undefined) {
        // Recorded even when the registration was refused: a node the save path does not count is a
        // node the next save drops from the stored html.
        recordLoaded();
        if (insertedThisSession.delete(id) && error) {
          const pos = getPos();
          const failedNode = pos === undefined ? undefined : editor.state.doc.nodeAt(pos);
          removeNode();
          if (failedNode && editor.state.doc.nodeAt(pos!) === failedNode) {
            throw new Error('The refused registration could not be removed from the editor.');
          }
        }
      }
    });
    // `registration` in the deps: a landing registration is recorded without waiting for a remount.
  }, [id, loadedId, isEdit, registration]);
};
